import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import hash from 'object-hash';
import Img from 'react-image';
import {
  FlexBox,
  Typography,
  Card,
  Box,
  Button,
  Backdrop,
  DotProgress,
  SelectDropdown,
  color
} from '@kis-technologies/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as clientSelectors } from 'store/clients';
import { actions as ciActions, selectors as ciSelectors } from 'store/clientIntegrations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { ImageUploadOverlay } from '../Shared/ImageUploadOverlay';
import { MediaTypes, MediaResponse, CIMediaProps } from '../../types';

const initialImage = {
  exists: false,
  loading: false,
  width: 0,
  height: 0
};

export const VenueImages: React.FC<CIMediaProps> = (props: CIMediaProps) => {
  const selectPending = useSelector(ciSelectors.selectPending);
  const venues = useSelector(ciSelectors.selectGroupedVenues);
  const { pending, successful } = useSelector(mediaSelectors.selectAction);
  const clientId = useSelector(clientSelectors.selectClientId);
  const [getTicketingEvents, setVenue, getMedia, deleteMedia, resetAction] = useActions([
    ciActions.getTicketingEvents,
    mediaActions.setVenue,
    mediaActions.getMedia,
    mediaActions.deleteMedia,
    mediaActions.resetAction
  ]);

  const selectedVenue = useSelector(mediaSelectors.selectVenue);
  const [image, setImage] = useState<typeof initialImage>(initialImage);
  const venueImages = useSelector(mediaSelectors.selectVenueImages);
  const hasVenues = venues.length > 0;
  const loading = image.loading || pending;
  const [showImageUploadOverlay, setShowImageUploadOverlay] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageName, setImageName] = useState<string | undefined>('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  React.useEffect(() => {
    setPreviewUrl('');
    setImageName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (props.environment) {
      setPreviewUrl('');
      setImageName('');
      getTicketingEvents(props.environment.id);
      getMedia({ type: MediaTypes.VENUE, clientIntegrationId: props.environment.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.environment]);

  React.useEffect(() => {
    if (props.environment && successful) {
      setPreviewUrl('');
      setImageName('');
      getMedia({ type: MediaTypes.VENUE, clientIntegrationId: props.environment.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  const getImageProperties = () => {
    setImage({ ...image, loading: true });
    const img = new Image();
    img.src = previewUrl;
    img.onload = () => {
      setImage({
        width: img.width,
        height: img.height,
        loading: false,
        exists: true
      });
    };
    img.onerror = () => setImage({ ...image, loading: false, exists: false });
  };

  React.useEffect(() => {
    if (selectedVenue && venueImages && venueImages.length) {
      const baseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/venue_images/`;
      const imgObj = venueImages.find((item: MediaResponse) => {
        return item.name === hash(selectedVenue.replace(new RegExp('/', 'g'), ' '));
      });
      if (imgObj && imgObj.s3Name) {
        setPreviewUrl(baseUrl.concat(imgObj.s3Name));
        setImageName(imgObj.imageName);
      } else {
        setPreviewUrl('');
        setImageName('');
      }
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, venueImages]);

  React.useEffect(() => {
    if (previewUrl) {
      getImageProperties();
    } else {
      setImage({ ...image, exists: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl]);

  const onDeleteImage = () => {
    if (venueImages && venueImages.length) {
      const imgObj = venueImages.find((item: MediaResponse) => {
        return item.name === hash(selectedVenue.replace(new RegExp('/', 'g'), ' '));
      });
      if (imgObj && imgObj.id) {
        deleteMedia(imgObj.id);
      }
    }
    setShowConfirmation(false);
  };

  return (
    <>
      {showConfirmation && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400}>
              <Typography fontSize={40} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography fontSize={24} align="center">
                Are you sure you want to delete this image?
              </Typography>
            </Box>
          }
          confirmText="Yes"
          onConfirm={onDeleteImage}
          cancelText="Cancel"
          onCancel={() => setShowConfirmation(false)}
          height={300}
          appearance="error"
        />
      )}
      {(selectPending || pending) && (
        <Backdrop open={selectPending || pending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      <FlexBox direction="column" fullWidth noWrap>
        {props.environment && hasVenues && (
          <>
            <FlexBox align="center" margin="0 0 6" direction="row">
              <Typography fontSize={24} fontWeight={700} sx={{ marginRight: '20px' }}>
                <Box width={180}>Select a Venue</Box>
              </Typography>
              <SelectDropdown
                items={venues.sort()}
                itemToString={(item: string) => item}
                selectedItem={selectedVenue}
                onChange={setVenue}
                placeholder="Select a venue"
              />
            </FlexBox>
            <FlexBox fullWidth direction="row">
              <Box width={650}>
                <Card raised hasBorderRadius>
                  <Card.Title
                    title={
                      <Typography fontSize={18} fontWeight={700}>
                        Venue Image
                      </Typography>
                    }
                    gutter={5}
                  />
                  {image.exists ? (
                    <>
                      <FlexBox fullWidth height={50} align="center" direction="row" sx={{ backgroundColor: '#EFEFEF' }}>
                        <Box fullHeight width={8} margin="0 8 0 0" sx={{ backgroundColor: '#f46d2e' }} />
                        <Typography fontSize={18}>{selectedVenue}</Typography>
                      </FlexBox>
                      <Card.Content>
                        <FlexBox fullWidth>
                          <Box width="45%" padding="0 2 0 0">
                            <Box margin="0 0 2">
                              <Typography fontSize={18}>File Details</Typography>
                            </Box>
                            {loading ? (
                              <Typography>Fetching details...</Typography>
                            ) : (
                              <>
                                {imageName && <Typography fontSize={14}>Name: {imageName}</Typography>}
                                <Typography fontSize={14}>
                                  Size: {image.height}px by {image.width}px
                                </Typography>
                              </>
                            )}
                          </Box>
                        </FlexBox>
                      </Card.Content>
                    </>
                  ) : (
                    <Card.Content>
                      <Box fullWidth margin="0 0 5">
                        <Box margin="0 0 4">
                          <Typography fontSize={18} fontWeight={400}>
                            Add one image to show the layout of the venue
                          </Typography>
                        </Box>
                        <Button
                          customSize="small"
                          width={180}
                          isLoading={loading}
                          disabled={!selectedVenue}
                          onClick={() => setShowImageUploadOverlay(true)}
                        >
                          Add Image
                        </Button>
                      </Box>
                    </Card.Content>
                  )}
                </Card>
              </Box>
              <Box margin="0 3" width={325}>
                <Card raised hasBorderRadius>
                  <Card.Title
                    title={
                      <Typography fontSize={18} fontWeight={600}>
                        Image Preview
                      </Typography>
                    }
                    gutter={5}
                  />
                  <Card.Content>
                    <FlexBox padding="2" align="center" justify="center">
                      <FlexBox width={200} height={150} align="center" justify="center">
                        {loading ? (
                          <FlexBox height={252} align="center">
                            <Typography>Fetching image...</Typography>
                          </FlexBox>
                        ) : (
                          <Img
                            src={previewUrl}
                            width="100%"
                            height="100%"
                            style={{ objectFit: 'cover' }}
                            unloader={<img alt="preview" src={NoImagePreview} width={180} height={158} />}
                          />
                        )}
                      </FlexBox>
                      <Box height={20} fullWidth />
                      {image.exists && (
                        <>
                          <Button
                            customSize="small"
                            width={250}
                            isLoading={loading}
                            onClick={() => setShowImageUploadOverlay(true)}
                          >
                            Select Different Image
                          </Button>
                          <Box fullWidth margin="2 0" />
                          <Button
                            customSize="small"
                            width={250}
                            appearance="white"
                            onClick={() => setShowConfirmation(true)}
                            isLoading={loading}
                            data-testid="delete-video-button"
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </FlexBox>
                  </Card.Content>
                </Card>
              </Box>
            </FlexBox>
          </>
        )}
      </FlexBox>
      {showImageUploadOverlay && (
        <ImageUploadOverlay
          imageType="venue"
          imageName={selectedVenue}
          clientIntegrationId={props.environment && props.environment.id}
          onCancel={() => setShowImageUploadOverlay(false)}
          onSuccess={() => setShowImageUploadOverlay(false)}
        />
      )}
    </>
  );
};
